import React, { useState, useEffect } from 'react';
import { Table, Card, Button, Spin, Tag, Divider, Modal, Form, Input, message, Upload } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import Navbar from '../../components/common/Navbar';
import Footer from '../../components/common/Footer';
import { Helmet } from 'react-helmet';
import api from '../../services/api';

const Career = () => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isApplyModalVisible, setIsApplyModalVisible] = useState(false);
  const [applyForm] = Form.useForm();
  const [applyLoading, setApplyLoading] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      const response = await api.get('/careers');
      if (response.data.success) {
        setJobs(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const showJobDetails = (job) => {
    setSelectedJob(job);
    setIsModalVisible(true);
  };

  const showApplyForm = (job) => {
    setSelectedJob(job);
    setIsApplyModalVisible(true);
    setFileList([]); // Reset file list when opening the form
  };

  const [resumeFileList, setResumeFileList] = useState([]);
  const [resumeUploading, setResumeUploading] = useState(false);

const resumeUploadProps = {
  name: 'resume',
  action: `${api.defaults.baseURL}/upload-resume`,
  fileList: resumeFileList,
  headers: {
    'Authorization': `Bearer ${localStorage.getItem('token')}` // if using auth
  },
  beforeUpload: (file) => {
    const isAllowedType = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
    ].includes(file.type);

    if (!isAllowedType) {
      message.error('You can only upload PDF, DOC or DOCX files!');
      return Upload.LIST_IGNORE; // This prevents the file from appearing in the fileList
    }

    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      message.error('File must be smaller than 5MB!');
      return Upload.LIST_IGNORE;
    }
    return true;
  },
  onChange: (info) => {
    setResumeFileList(info.fileList);
    
    if (info.file.status === 'uploading') {
      setResumeUploading(true);
      return;
    }
    
    if (info.file.status === 'done') {
      setResumeUploading(false);
      if (info.file.response && info.file.response.success) {
        message.success('Resume uploaded successfully!');
        applyForm.setFieldsValue({
          resume_link: info.file.response.fileUrl
        });
      } else {
        message.error(info.file.response?.message || 'Upload failed!');
      }
    }
    
    if (info.file.status === 'error') {
      setResumeUploading(false);
      message.error(info.file.response?.message || 'Upload failed!');
    }
    
    if (info.file.status === 'removed') {
      applyForm.setFieldsValue({
        resume_link: ''
      });
      setResumeFileList([]);
    }
  },
};
  const handleApply = async (values) => {
    try {
      if (!values.resume_link) {
        message.error('Please upload your resume!');
        return;
      }

      setApplyLoading(true);
      const formData = {
        ...values,
        job_id: selectedJob.id,
        job_title: selectedJob.title
      };

      const response = await api.post('/apply-now', formData);
      if (response.data.success) {
        message.success('Application submitted successfully!');
        setIsApplyModalVisible(false);
        applyForm.resetFields();
        setFileList([]);
      } else {
        message.error(response.data.message || 'Application failed');
      }
    } catch (error) {
      message.error(error.response?.data?.message || 'Application failed');
    } finally {
      setApplyLoading(false);
    }
  };

  const columns = [
    {
      title: 'Job Title',
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: 'Type',
      dataIndex: 'job_type',
      key: 'job_type',
      render: type => <Tag color={type === 'Full-time' ? 'blue' : 'green'}>{type}</Tag>
    },
    {
      title: 'Location',
      dataIndex: 'location',
      key: 'location',
    },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'status',
      render: active => (
        <Tag color={active ? 'green' : 'red'}>
          {active ? 'Active' : 'Inactive'}
        </Tag>
      )
    },
    {
      title: 'Action',
      key: 'action',
      render: (_, record) => (
        <>
          <Button type="primary" onClick={() => showJobDetails(record)}>
            View Details
          </Button>
          {record.is_active && (
            <Button
              type="primary"
              style={{ marginLeft: 8, background: '#52c41a', borderColor: '#52c41a' }}
              onClick={() => showApplyForm(record)}
            >
              Apply Now
            </Button>
          )}
        </>
      ),
    },
  ];

  return (
    <div className="home-page">
      <Helmet>
        <title>Careers - Startup Business Partners</title>
      </Helmet>

      <Navbar />

      <section className="breadcrumb_area breadcrumb2 bgimage biz_overlay">
        <div className="bg_image_holder" style={{ backgroundImage: 'url(https://snmtechnologies.in/startup/img/breadbg.jpg)', opacity: 1 }}>
          <img src="https://snmtechnologies.in/startup/img/breadbg.jpg" alt="" />
        </div>
        <div className="container content_above">
          <div className="row">
            <div className="col-md-12">
              <div className="breadcrumb_wrapper d-flex flex-column align-items-center">
                <h4 className="page_title">Careers</h4>
                <nav aria-label="breadcrumb">
                  <ol className="breadcrumb m-bottom-30">
                    <li className="breadcrumb-item"><a href="/">Home</a></li>
                    <li className="breadcrumb-item active" aria-current="page">Careers</li>
                  </ol>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="p-top-100 p-bottom-70">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <Card title="Current Openings">
                {loading ? (
                  <div className="text-center">
                    <Spin size="large" />
                  </div>
                ) : (
                  <Table
                    columns={columns}
                    dataSource={jobs}
                    rowKey="id"
                    pagination={{ pageSize: 10 }}
                  />
                )}
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Job Details Modal */}
      <Modal
        title={selectedJob?.title}
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={[
          <Button key="back" onClick={() => setIsModalVisible(false)}>
            Close
          </Button>,
          selectedJob?.is_active && (
            <Button
              key="apply"
              type="primary"
              onClick={() => {
                setIsModalVisible(false);
                showApplyForm(selectedJob);
              }}
            >
              Apply Now
            </Button>
          )
        ]}
        width={800}
      >
        {selectedJob && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <Tag color={selectedJob.job_type === 'Full-time' ? 'blue' : 'green'}>
                {selectedJob.job_type}
              </Tag>
              <Tag color={selectedJob.is_active ? 'green' : 'red'}>
                {selectedJob.is_active ? 'Active' : 'Inactive'}
              </Tag>
              <Tag>{selectedJob.location}</Tag>
            </div>

            <Divider orientation="left">Job Description</Divider>
            <div dangerouslySetInnerHTML={{ __html: selectedJob.description }} />

            {selectedJob.requirements && (
              <>
                <Divider orientation="left">Requirements</Divider>
                <div dangerouslySetInnerHTML={{ __html: selectedJob.requirements }} />
              </>
            )}

            {selectedJob.responsibilities && (
              <>
                <Divider orientation="left">Responsibilities</Divider>
                <div dangerouslySetInnerHTML={{ __html: selectedJob.responsibilities }} />
              </>
            )}
          </div>
        )}
      </Modal>

      {/* Apply Now Modal */}
      <Modal
        title={`Apply for ${selectedJob?.title}`}
        visible={isApplyModalVisible}
        onCancel={() => setIsApplyModalVisible(false)}
        footer={null}
      >
        <Form
          form={applyForm}
          layout="vertical"
          onFinish={handleApply}
        >
          <Form.Item
            name="name"
            label="Full Name"
            rules={[{ required: true, message: 'Please input your full name!' }]}
          >
            <Input placeholder="John Doe" />
          </Form.Item>

          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Please input your email!' },
              { type: 'email', message: 'Please enter a valid email!' }
            ]}
          >
            <Input placeholder="your.email@example.com" />
          </Form.Item>

          <Form.Item
            name="phone"
            label="Phone Number"
            rules={[{ required: true, message: 'Please input your phone number!' }]}
          >
            <Input placeholder="+1 234 567 8900" />
          </Form.Item>

          <Form.Item
            name="resume_link"
            label="Upload Resume"
            rules={[{ required: true, message: 'Please upload your resume!' }]}
          >
            <Upload {...resumeUploadProps}>
              <Button icon={<UploadOutlined />} loading={resumeUploading}>
                Click to Upload (PDF, DOC, DOCX)
              </Button>
            </Upload>
            <div style={{ marginTop: 8, fontSize: 12, color: '#999' }}>
              Max file size: 5MB. Accepted formats: PDF, DOC, DOCX
            </div>
          </Form.Item>

          <Form.Item
            name="cover_letter"
            label="Cover Letter"
            rules={[{ required: true, message: 'Please write a cover letter!' }]}
          >
            <Input.TextArea rows={4} placeholder="Explain why you're a good fit for this position..." />
          </Form.Item>

          <Form.Item>
            <Button
              type="primary"
              htmlType="submit"
              loading={applyLoading}
              disabled={uploading}
            >
              Submit Application
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      <Footer />
    </div>
  );
};

export default Career;